import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../../api';
import imageCompression from 'browser-image-compression';
import TenantAdminLayout from '../../Layout/TenantAdminLayout';
import { useStudents } from '../hooks/useStudents';
import { 
  Upload, User, Home, GraduationCap, ChevronLeft, ChevronRight,
  Eye, Trash2, Calendar, Phone, Mail, BookOpen, 
  Hash, MapPin, AlertCircle, Plus, Save, X, Shield, FileCheck, Info
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// ==========================================
// CONSTANTS
// ==========================================

const MAX_EDUCATION = 6;
const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Jain', 'Buddhist', 'Parsi', 'Jewish'];
const MOTHER_TONGUES = ['Kannada', 'English', 'Hindi', 'Telugu', 'Tamil', 'Malayalam', 'Marathi', 'Urdu', 'Bengali', 'Gujarati'];
const NATIONALITIES = ['Indian', 'NRI', 'American', 'British', 'Canadian', 'Australian'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

const EMPTY_EDU = {
  institutionName: '', previousClassGrade: '', yearOfPassing: '', 
  boardOrUniversity: '', registrationNumber: '', result: '', 
  mediumOfInstruction: '', transferCertificateNumber: '',
  transferCertificateDate: '', maxMarks: '', obtainedMarks: '', 
  educationProof: null
};

// ==========================================
// HELPER COMPONENTS
// ==========================================

const InputGroup = ({ icon: Icon, label, required, children, error }) => (
  <div className="space-y-1.5 w-full group">
    <label className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider group-focus-within:text-blue-600 transition-colors">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 group-focus-within:text-blue-500" />}
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {children}
    </div>
    {error && <p className="text-xs text-red-500 mt-1 font-medium flex items-center gap-1"><AlertCircle className="w-3 h-3"/>{error}</p>}
  </div>
);

const StyledSelect = ({ icon: Icon, error, ...props }) => (
  <div className="relative">
    <select 
      className={`w-full pl-9 pr-8 py-2.5 bg-white border ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'} text-gray-800 text-sm rounded-lg focus:ring-4 outline-none transition-all appearance-none cursor-pointer shadow-sm`}
      {...props}
    />
    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
      {Icon && <Icon className="w-4 h-4" />}
    </div>
    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
      <ChevronRight className="w-3.5 h-3.5 rotate-90" />
    </div>
  </div>
);

const StyledInput = ({ icon: Icon, error, ...props }) => (
  <div className="relative">
    <input 
      className={`w-full pl-9 pr-3 py-2.5 bg-white border ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'} text-gray-800 text-sm rounded-lg focus:ring-4 outline-none transition-all placeholder:text-gray-400 shadow-sm disabled:bg-gray-50 disabled:text-gray-500`}
      {...props}
    />
    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
      {Icon && <Icon className="w-4 h-4" />}
    </div>
  </div>
);

const SelectWithOther = ({ label, name, value, options, onChange, required, icon }) => {
  const isCustom = value && !options.includes(value);
  const [mode, setMode] = useState(isCustom ? 'custom' : 'select');

  useEffect(() => {
    if (value && !options.includes(value)) setMode('custom');
    else if (!value) setMode('select');
  }, [value, options]);

  const handleSelectChange = (e) => {
    const val = e.target.value;
    if (val === 'OTHER_OPTION') {
      setMode('custom');
      onChange({ target: { name, value: '' } });
    } else {
      setMode('select');
      onChange(e);
    }
  };

  return (
    <InputGroup label={label} required={required} icon={icon}>
      {mode === 'select' ? (
        <StyledSelect name={name} value={value} onChange={handleSelectChange} icon={icon}>
          <option value="">Select {label}</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          <option value="OTHER_OPTION" className="font-bold text-blue-600">+ Add Other</option>
        </StyledSelect>
      ) : (
        <div className="relative flex items-center gap-2">
           <StyledInput 
             name={name} 
             value={value} 
             onChange={onChange} 
             placeholder={`Type ${label}...`} 
             autoFocus
             icon={icon}
           />
           <button 
            type="button"
            onClick={() => { setMode('select'); onChange({ target: { name, value: '' } }); }}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            title="Back to list"
           >
             <X className="w-4 h-4" />
           </button>
        </div>
      )}
    </InputGroup>
  );
};

const ToggleSwitch = ({ checked, onChange, label }) => (
  <label className="inline-flex items-center cursor-pointer gap-2 select-none group">
    <div className="relative">
      <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
    </div>
    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide group-hover:text-blue-600 transition-colors">{label}</span>
  </label>
);

const SectionHeader = ({ title, icon: Icon, description }) => (
  <div className="flex items-center gap-3 pb-4 mb-6 border-b border-gray-100">
    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h3 className="text-lg font-bold text-gray-800 leading-none">{title}</h3>
      {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
    </div>
  </div>
);

const PreviewModal = ({ isOpen, onClose, fileUrl, fileType }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-xl overflow-hidden shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col relative" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center bg-gray-50">
          <h3 className="font-bold text-gray-700">Document Preview</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full"><X className="w-5 h-5"/></button>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-gray-100 flex items-center justify-center">
          {fileType?.startsWith('image/') || fileUrl?.match(/\.(jpeg|jpg|gif|png)$/i) ? (
            <img src={fileUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain rounded shadow" />
          ) : (
             <iframe src={`${fileUrl}#toolbar=0`} title="PDF Preview" className="w-full h-[65vh] rounded border bg-white" />
          )}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// PHONE VALIDATION HELPERS
// ==========================================

const isValidPhoneNumber = (phone) => {
  if (!phone) return false;
  try {
    const phoneNumber = parsePhoneNumberFromString('+' + phone);
    return phoneNumber && phoneNumber.isValid();
  } catch {
    return false;
  }
};

const getCountryCode = (phone) => {
  if (!phone) return '';
  try {
    const phoneNumber = parsePhoneNumberFromString('+' + phone);
    return phoneNumber ? '+' + phoneNumber.countryCallingCode : '';
  } catch {
    return '';
  }
};

const getNationalNumber = (phone) => {
  if (!phone) return '';
  try {
    const phoneNumber = parsePhoneNumberFromString('+' + phone);
    return phoneNumber ? phoneNumber.nationalNumber : '';
  } catch {
    return '';
  }
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const EditStudent = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = location.state || {};

  // --- Academic Context Hook ---
  const {
    academicYears, semesters, classes: availableClasses, isSemesterBased,
    selectedAcademicYear, selectedSemester, handleAcademicYearChange,
    handleSemesterChange, handleClassChange, setSelectedClass, loading: contextLoading
  } = useStudents(tenantId);

  // --- Profile & Education ---
  const [profile, setProfile] = useState({
    permanentAddress: { houseNoStreet: '', cityTownVillage: '', stateProvince: '', postalZipCode: '', country: '' },
    correspondenceAddress: { houseNoStreet: '', cityTownVillage: '', stateProvince: '', postalZipCode: '', country: '' },
    emergencyContactPerson: '', emergencyContactNumber: '', previousEducation: [], otherDetails: {},
    religion: '', motherTongue: '', nationality: '', bloodGroup: '', hasDisability: false, disabilityType: ''
  });

  // --- Terms State for Non-Semester-Based ---
  const [terms, setTerms] = useState([]);

  useEffect(() => {
    if (!selectedAcademicYear || isSemesterBased === undefined) return;
    if (isSemesterBased) {
      setTerms([]);
    } else {
      const fetchTerms = async () => {
        try {
          const termRes = await api.get(`/api/academic-year/${selectedAcademicYear}/terms?tenantId=${tenantId}`);
          const fetchedTerms = termRes.data.terms || [];
          setTerms(fetchedTerms);
          if (profile.term && fetchedTerms.length > 0) {
            const found = fetchedTerms.find(t => t._id === profile.term);
            if (!found && fetchedTerms.length > 0) {
              setProfile(prev => ({ ...prev, term: fetchedTerms[0]._id }));
            }
          } else if (fetchedTerms.length > 0) {
            setProfile(prev => ({ ...prev, term: fetchedTerms[0]._id }));
          }
        } catch (e) {
          setTerms([]);
        }
      };
      fetchTerms();
    }
  }, [selectedAcademicYear, isSemesterBased, tenantId, profile.term]);

  // --- State ---
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Dropdown Data
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', studentPhoneNumber: '',
    parentName: '', parentPhoneNumber: '', parentEmail: '',
    USN: '', admissionDate: '', gender: '', dateOfBirth: '',
    address: '', emergencyContact: '', bloodGroup: '', nationality: '',
    category: '', transportRequired: false, hostelRequired: false, remarks: ''
  });
  
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [selectedProgramTypeId, setSelectedProgramTypeId] = useState('');
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [uiClassId, setUiClassId] = useState('');
  const [errors, setErrors] = useState({});

  // Phone fields (with country code)
  const [studentPhone, setStudentPhone] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');

  const [copyAddress, setCopyAddress] = useState(false);
  const [educationList, setEducationList] = useState([]);
  const [educationProofFiles, setEducationProofFiles] = useState([]);
  
  // Preview Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPreview, setCurrentPreview] = useState(null);

  // Store refs for academic context handlers
  const academicHandlersRef = useRef({
    handleAcademicYearChange,
    handleSemesterChange,
    setSelectedClass
  });

  useEffect(() => {
    academicHandlersRef.current = {
      handleAcademicYearChange,
      handleSemesterChange,
      setSelectedClass
    };
  }, [handleAcademicYearChange, handleSemesterChange, setSelectedClass]);

  // --- Validation ---
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.firstName) newErrors.firstName = "Required";
    if (!formData.lastName) newErrors.lastName = "Required";
    if (!formData.email) newErrors.email = "Required";
    if (!studentPhone || !isValidPhoneNumber(studentPhone)) {
      newErrors.studentPhoneNumber = "Enter a valid phone number";
      isValid = false;
    }
    if (!formData.parentName) newErrors.parentName = "Required";
    if (!parentPhone || !isValidPhoneNumber(parentPhone)) {
      newErrors.parentPhoneNumber = "Enter a valid phone number";
      isValid = false;
    }
    if (formData.parentEmail && !formData.parentEmail.includes('@')) {
      newErrors.parentEmail = "Invalid email format";
      isValid = false;
    }
    if (!selectedProgramId) newErrors.program = "Required";
    if (!selectedAcademicYear) newErrors.academicYear = "Required";

    if (!profile.emergencyContactPerson) newErrors.emergencyPerson = "Required";
    if (!emergencyPhone || !isValidPhoneNumber(emergencyPhone)) {
      newErrors.emergencyNumber = "Enter a valid phone number";
      isValid = false;
    }
    if (!profile.permanentAddress.houseNoStreet) newErrors.houseNo = "Required";
    if (!profile.permanentAddress.cityTownVillage) newErrors.city = "Required";
    if (!profile.permanentAddress.stateProvince) newErrors.state = "Required";
    if (!profile.permanentAddress.postalZipCode) newErrors.zip = "Required";
    if (!profile.permanentAddress.country) newErrors.country = "Required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill all mandatory fields marked *");
      isValid = false;
    }
    return isValid;
  };

  // --- Data Loading ---
  useEffect(() => {
    if (!studentId || dataFetched) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setPhotoError(false);
        
        const studentRes = await api.get(`/api/students/by-id/${studentId}?tenantId=${tenantId}`);
        const studentData = studentRes.data;
        setStudent(studentData);
        
        // Set photo preview with proper validation
        if (studentData.profilePhoto && studentData.profilePhoto.fileId) {
          const timestamp = studentData.profilePhoto.uploadedAt 
            ? new Date(studentData.profilePhoto.uploadedAt).getTime() 
            : new Date().getTime();
          
          const photoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/student-profile/${studentId}?t=${timestamp}`;
          setPhotoPreview(photoUrl);
          console.log('[EditStudent] Profile photo source:', photoUrl, studentData);
        } else {
          console.log('[EditStudent] No profile photo found in database');
          setPhotoPreview('');
        }
        
        setFormData({
          firstName: studentData.firstName || '',
          lastName: studentData.lastName || '',
          email: studentData.email || '',
          parentName: studentData.parentName || '',
          parentEmail: studentData.parentEmail || '',
          USN: studentData.USN || '',
          admissionDate: studentData.admissionDate || '',
          gender: studentData.gender || '',
          dateOfBirth: studentData.dateOfBirth || '',
          address: studentData.address || '',
          emergencyContact: studentData.emergencyContact || '',
          bloodGroup: studentData.bloodGroup || '',
          nationality: studentData.nationality || '',
          category: studentData.category || '',
          transportRequired: studentData.transportRequired || false,
          hostelRequired: studentData.hostelRequired || false,
          remarks: studentData.remarks || ''
        });

        const profileRes = await api.get(`/api/students/profile/${studentId}`);
        const profileData = profileRes.data.profile || profileRes.data;
        
        setStudentPhone(profileData.phoneNumber ? (profileData.phoneNumber.countryCode?.replace('+','') + profileData.phoneNumber.number) : '');
        setParentPhone(profileData.parentPhoneNumber ? (profileData.parentPhoneNumber.countryCode?.replace('+','') + profileData.parentPhoneNumber.number) : '');
        setEmergencyPhone(profileData.emergencyContactNumber ? (profileData.emergencyContactNumber.countryCode?.replace('+','') + profileData.emergencyContactNumber.number) : '');

        setProfile({
          ...profileData,
          emergencyContactPerson: profileData.emergencyContactPerson || '',
          permanentAddress: profileData.permanentAddress || { houseNoStreet: '', cityTownVillage: '', stateProvince: '', postalZipCode: '', country: '' },
          correspondenceAddress: profileData.correspondenceAddress || { houseNoStreet: '', cityTownVillage: '', stateProvince: '', postalZipCode: '', country: '' },
          religion: profileData.religion || '',
          motherTongue: profileData.motherTongue || '',
          nationality: profileData.nationality || '',
          bloodGroup: profileData.bloodGroup || '',
          hasDisability: profileData.hasDisability || false,
          disabilityType: profileData.disabilityType || ''
        });

        const eduList = profileData.previousEducation || [];
        setEducationList(eduList);
        setEducationProofFiles(new Array(eduList.length).fill(null));

        const [ptRes, progRes] = await Promise.all([
          api.get(`/api/program-types?tenantId=${studentData.tenantId}`),
          api.get(`/api/programs?tenantId=${studentData.tenantId}`)
        ]);
        setProgramTypes(ptRes.data.programTypes || []);
        const allPrograms = progRes.data.programs || [];
        setPrograms(allPrograms);

        const progId = studentData.programId?.$oid || studentData.programId;
        let pTypeId = studentData.programTypeId;
        if (!pTypeId && progId) {
          const p = allPrograms.find(item => item._id === progId);
          if (p) pTypeId = p.programTypeId?._id || p.programTypeId;
        }
        setSelectedProgramTypeId(pTypeId || '');
        setSelectedProgramId(progId || '');
        if (pTypeId) setFilteredPrograms(allPrograms.filter(p => String(p.programTypeId?._id || p.programTypeId) === String(pTypeId)));
        else setFilteredPrograms(allPrograms);

        setTimeout(() => {
          if (academicHandlersRef.current.handleAcademicYearChange) {
            academicHandlersRef.current.handleAcademicYearChange(studentData.academicYearId);
          }
          if (studentData.semesterId && academicHandlersRef.current.handleSemesterChange) {
            academicHandlersRef.current.handleSemesterChange(studentData.semesterId.$oid || studentData.semesterId);
          }
          if (studentData.classId && academicHandlersRef.current.setSelectedClass) {
            academicHandlersRef.current.setSelectedClass(studentData.classId);
          }
        }, 0);
        
        if (studentData.classId) {
          setUiClassId(studentData.classId);
        }

        setDataFetched(true);

      } catch (e) { 
        console.error('Failed to load student data:', e);
        toast.error("Failed to load student data"); 
      } finally { 
        setLoading(false); 
      }
    };

    fetchData();
  }, [studentId, dataFetched, tenantId, refreshKey]);

  // --- Handlers ---

  useEffect(() => {
    if (copyAddress) {
      setProfile(prev => ({ ...prev, correspondenceAddress: { ...prev.permanentAddress } }));
    }
  }, [copyAddress, profile.permanentAddress]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleProfileInput = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProgramTypeChange = async (e) => {
    const val = e.target.value;
    setSelectedProgramTypeId(val);
    const filtered = programs.filter(p => String(p.programTypeId?._id || p.programTypeId) === String(val));
    setFilteredPrograms(filtered);
    setSelectedProgramId(''); 
  };

  const handleFileChange = async (e, type, idx = null) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (type === 'photo') {
      try {
        const compressed = await imageCompression(file, { maxSizeMB: 0.1, maxWidthOrHeight: 1024, useWebWorker: true });
        const newFile = new File([compressed], file.name, { type: file.type });
        setPhotoFile(newFile);
        setPhotoError(false);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result);
          console.log('[EditStudent] New photo preview set');
        };
        reader.readAsDataURL(newFile);
      } catch (e) { 
        console.error('[EditStudent] Image compression error:', e);
        toast.error("Image error"); 
      }
    } else if (type === 'eduProof') {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedFiles = [...educationProofFiles];
        updatedFiles[idx] = { file, preview: reader.result, type: file.type, name: file.name, size: file.size };
        setEducationProofFiles(updatedFiles);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageError = () => {
    console.log('[EditStudent] Image failed to load');
    setPhotoError(true);
    setPhotoPreview('');
    setPhotoFile(null);
    toast.info('Profile photo not found. You can upload a new one.');
  };

  const handleEduChange = (idx, field, val) => {
    const updated = [...educationList];
    updated[idx][field] = val;
    setEducationList(updated);
  };

  const removeEducation = (idx) => {
    setEducationList(l => l.filter((_, i) => i !== idx));
    setEducationProofFiles(f => f.filter((_, i) => i !== idx));
  };

  const removeProof = async (idx) => {
    if (educationProofFiles[idx]) {
      const updatedFiles = [...educationProofFiles];
      updatedFiles[idx] = null;
      setEducationProofFiles(updatedFiles);
      return;
    }
    if (educationList[idx].educationProof) {
      try {
        await api.delete(`/api/files/student/${studentId}/education/${idx}/proof`);
        const updatedList = [...educationList];
        updatedList[idx].educationProof = null;
        setEducationList(updatedList);
        toast.success("Proof removed");
      } catch (e) { toast.error("Failed to remove proof"); }
    }
  };

  const openPreview = (idx) => {
    if (educationProofFiles[idx]) {
      setCurrentPreview({ url: educationProofFiles[idx].preview, type: educationProofFiles[idx].type });
    } else if (educationList[idx].educationProof) {
      setCurrentPreview({ 
        url: `${process.env.REACT_APP_BACKEND_URL}${educationList[idx].educationProof.fileUrl}`, 
        type: educationList[idx].educationProof.fileType || 'image/jpeg'
      });
    }
    setModalOpen(true);
  };

  // ==========================================
  // ✅ FIXED: handleFinalSubmit with parentPhoneNumber
  // ==========================================
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // ===== 1. CORE PAYLOAD =====
      const payload = new FormData();
      
      // ✅ FIXED: Include parentPhoneNumber and parentCountryCode
      const fieldsToSend = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        parentName: formData.parentName,
        parentEmail: formData.parentEmail,
        parentPhoneNumber: getNationalNumber(parentPhone),  // ✅ ADDED - FIX
        parentCountryCode: getCountryCode(parentPhone),      // ✅ ADDED - FIX
        USN: formData.USN,
        admissionDate: formData.admissionDate,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        address: formData.address,
        emergencyContact: formData.emergencyContact,
        bloodGroup: formData.bloodGroup,
        nationality: formData.nationality,
        category: formData.category,
        transportRequired: formData.transportRequired,
        hostelRequired: formData.hostelRequired,
        remarks: formData.remarks,
        tenantId: tenantId
      };

      // Debug log to verify
      console.log('[EditStudent] 📤 Sending parent fields:', {
        parentName: fieldsToSend.parentName,
        parentEmail: fieldsToSend.parentEmail,
        parentPhoneNumber: fieldsToSend.parentPhoneNumber,
        parentCountryCode: fieldsToSend.parentCountryCode
      });

      // Add each field to FormData
      Object.entries(fieldsToSend).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          payload.append(key, String(value));
        }
      });

      // Add academic context (using state values)
      if (selectedAcademicYear) {
        payload.append('academicYearId', String(selectedAcademicYear));
      }
      
      if (selectedProgramId) {
        payload.append('programId', String(selectedProgramId));
      }
      
      if (isSemesterBased && selectedSemester) {
        payload.append('semesterId', String(selectedSemester));
      }
      
      if (uiClassId) {
        payload.append('classId', String(uiClassId));
      }
      
      // Add photo if changed
      if (photoFile) {
        payload.append('profilePhoto', photoFile);
        console.log('[EditStudent] Appending profilePhoto:', photoFile.name);
      }

      console.log('[EditStudent] Sending core update');

      // Send core update
      const updateResponse = await api.put(`/api/students/${studentId}`, payload, { 
        headers: {'Content-Type': 'multipart/form-data'} 
      });

      console.log('[EditStudent] Core update response:', updateResponse.data);

      // ===== 2. PROFILE PAYLOAD =====
      const eduListForSave = educationList.map(edu => edu);
      const { verificationStatus, ...profileWithoutVerification } = profile;
      
      const profilePayload = {
        ...profileWithoutVerification,
        previousEducation: eduListForSave,
        tenantId,
        studentId,
        academicYearId: selectedAcademicYear,
        phoneNumber: { 
          number: getNationalNumber(studentPhone), 
          countryCode: getCountryCode(studentPhone) 
        },
        parentPhoneNumber: { 
          number: getNationalNumber(parentPhone), 
          countryCode: getCountryCode(parentPhone) 
        },
        emergencyContactNumber: { 
          number: getNationalNumber(emergencyPhone), 
          countryCode: getCountryCode(emergencyPhone) 
        },
        bloodGroup: profile.bloodGroup,
        religion: profile.religion,
        motherTongue: profile.motherTongue,
        nationality: profile.nationality,
        preferredMediumOfInstruction: profile.preferredMediumOfInstruction || 'English'
      };

      await api.post(`/api/students/profile/${studentId}`, profilePayload);

      // ===== 3. UPLOAD NEW PROOFS =====
      for (let idx = 0; idx < educationProofFiles.length; idx++) {
        const proofObj = educationProofFiles[idx];
        if (proofObj && proofObj.file) {
          const fd = new FormData();
          fd.append('educationProof', proofObj.file);
          await api.post(`/api/files/student/${studentId}/education/${idx}/proof`, fd, { 
            headers: { 'Content-Type': 'multipart/form-data' } 
          });
        }
      }

      // Refresh the photo preview after successful upload
      if (photoFile) {
        setRefreshKey(prev => prev + 1);
      }

      toast.success("All changes saved successfully!");
      setTimeout(() => navigate(-1), 1000);

    } catch (e) {
      console.error('Save error:', e);
      console.error('Error response:', e.response?.data);
      toast.error(e.response?.data?.message || "Failed to save changes");
    } finally { 
      setIsSubmitting(false); 
    }
  };

  // Show loading
  if ((loading || contextLoading) && !student) {
    return (
      <TenantAdminLayout>
        <div className="flex h-[80vh] items-center justify-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </TenantAdminLayout>
    );
  }

  if (!student) {
    return (
      <TenantAdminLayout>
        <div className="flex h-[80vh] items-center justify-center flex-col gap-4">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-gray-600">Failed to load student data</p>
          <button 
            onClick={() => navigate(-1)} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </TenantAdminLayout>
    );
  }

  const currentClassName = availableClasses.find(c => c._id === uiClassId)?.className || "Unallocated (In Pool)";

  return (
    <TenantAdminLayout>
      <div className="min-h-screen bg-gray-50/50 pb-24 relative">
        <ToastContainer position="top-right" autoClose={3000} />
        
        <PreviewModal 
           isOpen={modalOpen} 
           onClose={() => setModalOpen(false)} 
           fileUrl={currentPreview?.url} 
           fileType={currentPreview?.type} 
        />

        <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"><ChevronLeft className="w-5 h-5"/></button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{student.firstName} {student.lastName}</h1>
                <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">{studentId}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={handleFinalSubmit} 
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg shadow-lg shadow-blue-200 hover:bg-blue-700 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> Saving...</> : <><Save className="w-4 h-4"/> Save Changes</>}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
          
          <div className={`rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 border ${uiClassId ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${uiClassId ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                <Home className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider opacity-70">{uiClassId ? 'Assigned Class' : 'Status'}</p>
                <p className={`font-bold text-lg ${uiClassId ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {uiClassId ? currentClassName : 'Unallocated (In Pool)'}
                </p>
              </div>
            </div>
            <div className="w-full md:w-auto flex items-center gap-3">
              <span className="text-xs font-bold text-gray-500 uppercase">Change:</span>
              <div className="relative">
                <select 
                  className="pl-3 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                  value={uiClassId}
                  onChange={e => { 
                    const newVal = e.target.value;
                    setUiClassId(newVal); 
                    handleClassChange(newVal); 
                  }}
                >
                  <option value="">Move to Pool</option>
                  {availableClasses.map(c => <option key={c._id} value={c._id}>{c.className}</option>)}
                </select>
                <ChevronRight className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none"/>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm text-center">
                <div className="w-32 h-32 mx-auto rounded-full bg-gray-100 border-4 border-white shadow-lg overflow-hidden relative group mb-4">
                  {photoPreview && !photoError ? (
                    <img 
                      src={photoPreview} 
                      className="w-full h-full object-cover" 
                      alt="Profile"
                      onError={handleImageError}
                      key={photoPreview}
                    />
                  ) : (
                    <User className="w-12 h-12 text-gray-300 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"/>
                  )}
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <label className="text-white text-xs font-bold cursor-pointer flex flex-col items-center">
                      <Upload className="w-5 h-5 mb-1"/> 
                      {photoError ? 'UPLOAD PHOTO' : 'CHANGE'}
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*" 
                        onChange={(e) => {
                          setPhotoError(false);
                          handleFileChange(e, 'photo');
                        }} 
                      />
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-400 font-medium">
                  {photoError ? 'Photo not found - upload new' : 'Click image to update'}
                </p>
                <div className="mt-6 pt-4 border-t border-gray-100 text-left">
                  <InputGroup label="USN ID" icon={Hash}>
                    <StyledInput name="USN" value={formData.USN} onChange={handleInput} placeholder="Enter USN" />
                  </InputGroup>
                </div>
              </div>
            </div>

            <div className="lg:col-span-9 space-y-8">
              
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <SectionHeader title="Academic Context" icon={GraduationCap} description="Program and year details" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <InputGroup label="Academic Year" icon={Calendar} required error={errors.academicYear}>
                    <StyledSelect value={selectedAcademicYear} onChange={e => handleAcademicYearChange(e.target.value)}>
                      {academicYears.map(y => <option key={y._id} value={y._id}>{y.name}</option>)}
                    </StyledSelect>
                  </InputGroup>
                  <InputGroup label="Program Type" icon={BookOpen} required>
                    <StyledSelect value={selectedProgramTypeId} onChange={handleProgramTypeChange}>
                      <option value="">Select Type</option>
                      {programTypes.map(pt => <option key={pt._id} value={pt._id}>{pt.name}</option>)}
                    </StyledSelect>
                  </InputGroup>
                  <InputGroup label="Program" icon={GraduationCap} required error={errors.program}>
                    <StyledSelect value={selectedProgramId} onChange={e => setSelectedProgramId(e.target.value)} disabled={!selectedProgramTypeId}>
                      <option value="">Select Program</option>
                      {filteredPrograms.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </StyledSelect>
                  </InputGroup>
                  {isSemesterBased ? (
                    <InputGroup label="Semester" icon={Hash} required>
                      <StyledSelect
                        value={selectedSemester}
                        onChange={e => handleSemesterChange(e.target.value)}
                      >
                        {semesters.map(s => (
                          <option key={s._id} value={s._id}>
                            {s.semesterName}
                          </option>
                        ))}
                      </StyledSelect>
                    </InputGroup>
                  ) : (
                    <InputGroup label="Term" required>
                      <StyledSelect
                        value={profile.term || ''}
                        onChange={e => setProfile(prev => ({ ...prev, term: e.target.value }))}
                      >
                        <option value="">Select Term</option>
                        {terms.map(t => (
                          <option key={t._id} value={t._id}>{t.termLabel}</option>
                        ))}
                      </StyledSelect>
                    </InputGroup>
                  )}
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <SectionHeader title="Personal Information" icon={User} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  <InputGroup label="First Name" icon={User} required error={errors.firstName}> 
                    <StyledInput name="firstName" value={formData.firstName} onChange={handleInput} /> 
                  </InputGroup>
                  <InputGroup label="Last Name" icon={User} required error={errors.lastName}> 
                    <StyledInput name="lastName" value={formData.lastName} onChange={handleInput} /> 
                  </InputGroup>
                  <InputGroup label="Email" icon={Mail} required error={errors.email}> 
                    <StyledInput type="email" name="email" value={formData.email} onChange={handleInput} /> 
                  </InputGroup>
                  <InputGroup label="Phone" icon={Phone} required error={errors.studentPhoneNumber}>
                    <PhoneInput
                      country={'in'}
                      value={studentPhone}
                      onChange={phone => {
                        setStudentPhone(phone);
                        if (errors.studentPhoneNumber) setErrors(prev => ({ ...prev, studentPhoneNumber: null }));
                      }}
                      inputProps={{ name: 'studentPhoneNumber', required: true }}
                      enableSearch
                      inputClass="w-full pl-12 pr-3 py-2.5 bg-white border border-gray-300 text-gray-800 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400 shadow-sm disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </InputGroup>
                  <InputGroup label="Gender" icon={User}>
                    <StyledSelect name="gender" value={formData.gender} onChange={handleInput}>
                      <option value="">Select</option><option value="male">Male</option><option value="female">Female</option>
                    </StyledSelect>
                  </InputGroup>
                  <InputGroup label="Date of Birth" icon={Calendar}> 
                    <StyledInput type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInput} /> 
                  </InputGroup>
                </div>

                <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 mb-6">
                   <h4 className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-2"><Info className="w-3.5 h-3.5"/> Demographics</h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <SelectWithOther label="Religion" name="religion" value={profile.religion} options={RELIGIONS} onChange={handleProfileInput} />
                      <SelectWithOther label="Mother Tongue" name="motherTongue" value={profile.motherTongue} options={MOTHER_TONGUES} onChange={handleProfileInput} />
                      <SelectWithOther label="Nationality" name="nationality" value={profile.nationality} options={NATIONALITIES} onChange={handleProfileInput} />
                      <SelectWithOther label="Blood Group" name="bloodGroup" value={profile.bloodGroup} options={BLOOD_GROUPS} onChange={handleProfileInput} />
                   </div>
                   <div className="flex items-center gap-3 pt-6">
                     <ToggleSwitch checked={profile.hasDisability} onChange={e => setProfile(p => ({...p, hasDisability: e.target.checked}))} label="Has Disability?" />
                     {profile.hasDisability && (
                       <div className="flex-1"><StyledInput placeholder="Disability Type" name="disabilityType" value={profile.disabilityType} onChange={handleProfileInput} /></div>
                     )}
                   </div>
                </div>
                
                <div className="bg-blue-50/50 p-5 rounded-xl border border-blue-100">
                  <h4 className="text-xs font-bold text-blue-700 uppercase mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5"/> Parent Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputGroup label="Parent Name" required error={errors.parentName}> 
                      <StyledInput name="parentName" value={formData.parentName} onChange={handleInput} /> 
                    </InputGroup>
                    <InputGroup label="Parent Phone" required error={errors.parentPhoneNumber}>
                      <PhoneInput
                        country={'in'}
                        value={parentPhone}
                        onChange={phone => {
                          setParentPhone(phone);
                          if (errors.parentPhoneNumber) setErrors(prev => ({ ...prev, parentPhoneNumber: null }));
                        }}
                        inputProps={{ name: 'parentPhoneNumber', required: true }}
                        enableSearch
                        inputClass="w-full pl-12 pr-3 py-2.5 bg-white border border-gray-300 text-gray-800 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400 shadow-sm disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </InputGroup>
                    <InputGroup label="Parent Email" error={errors.parentEmail}>
                      <StyledInput 
                        type="email"
                        name="parentEmail" 
                        value={formData.parentEmail} 
                        onChange={handleInput} 
                        placeholder="parent@example.com"
                      />
                    </InputGroup>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                  <SectionHeader title="Permanent Address" icon={Home} />
                  <div className="space-y-4">
                    <InputGroup label="Street" required error={errors.houseNo}> 
                      <StyledInput value={profile.permanentAddress.houseNoStreet || ''} onChange={e => setProfile(p=>({...p, permanentAddress: {...p.permanentAddress, houseNoStreet: e.target.value}}))} /> 
                    </InputGroup>
                    <div className="grid grid-cols-2 gap-4">
                      <InputGroup label="City" required error={errors.city}> 
                        <StyledInput value={profile.permanentAddress.cityTownVillage || ''} onChange={e => setProfile(p=>({...p, permanentAddress: {...p.permanentAddress, cityTownVillage: e.target.value}}))} /> 
                      </InputGroup>
                      <InputGroup label="State" required error={errors.state}> 
                        <StyledInput value={profile.permanentAddress.stateProvince || ''} onChange={e => setProfile(p=>({...p, permanentAddress: {...p.permanentAddress, stateProvince: e.target.value}}))} /> 
                      </InputGroup>
                      <InputGroup label="Zip" required error={errors.zip}> 
                        <StyledInput value={profile.permanentAddress.postalZipCode || ''} onChange={e => setProfile(p=>({...p, permanentAddress: {...p.permanentAddress, postalZipCode: e.target.value}}))} /> 
                      </InputGroup>
                      <InputGroup label="Country" required error={errors.country}> 
                        <StyledInput value={profile.permanentAddress.country || ''} onChange={e => setProfile(p=>({...p, permanentAddress: {...p.permanentAddress, country: e.target.value}}))} /> 
                      </InputGroup>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm relative">
                  <div className="flex justify-between items-start mb-6 border-b border-gray-100 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><MapPin className="w-5 h-5"/></div>
                      <h3 className="text-lg font-bold text-gray-800">Current Address</h3>
                    </div>
                    <ToggleSwitch checked={copyAddress} onChange={e => setCopyAddress(e.target.checked)} label="Sync" />
                  </div>
                  <div className={`space-y-4 transition-all duration-300 ${copyAddress ? 'opacity-50 pointer-events-none grayscale' : ''}`}>
                    <InputGroup label="Street"> 
                      <StyledInput value={profile.correspondenceAddress.houseNoStreet || ''} onChange={e => setProfile(p=>({...p, correspondenceAddress: {...p.correspondenceAddress, houseNoStreet: e.target.value}}))} /> 
                    </InputGroup>
                    <div className="grid grid-cols-2 gap-4">
                      <InputGroup label="City"> 
                        <StyledInput value={profile.correspondenceAddress.cityTownVillage || ''} onChange={e => setProfile(p=>({...p, correspondenceAddress: {...p.correspondenceAddress, cityTownVillage: e.target.value}}))} /> 
                      </InputGroup>
                      <InputGroup label="State"> 
                        <StyledInput value={profile.correspondenceAddress.stateProvince || ''} onChange={e => setProfile(p=>({...p, correspondenceAddress: {...p.correspondenceAddress, stateProvince: e.target.value}}))} /> 
                      </InputGroup>
                      <InputGroup label="Zip"> 
                        <StyledInput value={profile.correspondenceAddress.postalZipCode || ''} onChange={e => setProfile(p=>({...p, correspondenceAddress: {...p.correspondenceAddress, postalZipCode: e.target.value}}))} /> 
                      </InputGroup>
                      <InputGroup label="Country"> 
                        <StyledInput value={profile.correspondenceAddress.country || ''} onChange={e => setProfile(p=>({...p, correspondenceAddress: {...p.correspondenceAddress, country: e.target.value}}))} /> 
                      </InputGroup>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 p-6 rounded-2xl border border-red-100">
                <SectionHeader title="Emergency Contact" icon={AlertCircle} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputGroup label="Contact Person" icon={User} required error={errors.emergencyPerson}> 
                    <StyledInput 
                      value={profile.emergencyContactPerson || ''} 
                      onChange={e => setProfile(p=>({...p, emergencyContactPerson: e.target.value}))} 
                    /> 
                  </InputGroup>
                  <InputGroup label="Phone Number" icon={Phone} required error={errors.emergencyNumber}> 
                    <PhoneInput
                      country={'in'}
                      value={emergencyPhone}
                      onChange={phone => {
                        setEmergencyPhone(phone);
                        if (errors.emergencyNumber) setErrors(prev => ({ ...prev, emergencyNumber: null }));
                      }}
                      inputProps={{ name: 'emergencyContactNumber', required: true }}
                      enableSearch
                      inputClass="w-full pl-12 pr-3 py-2.5 bg-white border border-gray-300 text-gray-800 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400 shadow-sm disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-800">Education History</h3>
                  <div className="h-px bg-gray-200 flex-1"></div>
                </div>
                {educationList.map((edu, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                      <span className="text-sm font-bold text-gray-700">Record #{idx + 1}</span>
                      {educationList.length > 1 && <button type="button" onClick={() => removeEducation(idx)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>}
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <InputGroup label="Institution"> 
                          <StyledInput value={edu.institutionName} onChange={e => handleEduChange(idx, 'institutionName', e.target.value)} /> 
                        </InputGroup>
                        <InputGroup label="Grade/Class"> 
                          <StyledInput value={edu.previousClassGrade} onChange={e => handleEduChange(idx, 'previousClassGrade', e.target.value)} /> 
                        </InputGroup>
                        <InputGroup label="Passing Year"> 
                          <StyledInput value={edu.yearOfPassing} onChange={e => handleEduChange(idx, 'yearOfPassing', e.target.value)} /> 
                        </InputGroup>
                        <InputGroup label="Board"> 
                          <StyledInput value={edu.boardOrUniversity} onChange={e => handleEduChange(idx, 'boardOrUniversity', e.target.value)} /> 
                        </InputGroup>
                        <InputGroup label="Score"> 
                          <StyledInput value={edu.result} onChange={e => handleEduChange(idx, 'result', e.target.value)} /> 
                        </InputGroup>
                        <InputGroup label="Reg No"> 
                          <StyledInput value={edu.registrationNumber} onChange={e => handleEduChange(idx, 'registrationNumber', e.target.value)} /> 
                        </InputGroup>
                      </div>
                      
                      <div className="border-t border-gray-100 pt-4">
                        {!educationProofFiles[idx] && !edu.educationProof?.fileUrl ? (
                           <label className="flex items-center gap-3 px-4 py-3 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors w-full md:w-auto inline-flex">
                             <div className="bg-blue-50 p-2 rounded text-blue-600"><Upload className="w-4 h-4"/></div>
                             <div className="text-xs">
                               <span className="font-bold text-gray-700 block">Upload Proof</span>
                               <span className="text-gray-400">PDF/Image</span>
                             </div>
                             <input type="file" className="hidden" accept="image/*,application/pdf" onChange={e => handleFileChange(e, 'eduProof', idx)} />
                           </label>
                        ) : (
                          <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                                <div className="flex items-center gap-3 overflow-hidden">
                                  <FileCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-bold text-gray-800 truncate">
                                      {educationProofFiles[idx] ? educationProofFiles[idx].name : edu.educationProof.fileName || 'Uploaded Document'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {educationProofFiles[idx] ? 'Ready to upload' : 'Saved on server'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                   <button type="button" onClick={() => openPreview(idx)} className="px-3 py-1.5 bg-white border border-gray-200 rounded text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 shadow-sm">
                                     <Eye className="w-3.5 h-3.5"/> Preview
                                   </button>
                                   <button type="button" onClick={() => removeProof(idx)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><X className="w-4 h-4"/></button>
                                </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {educationList.length < MAX_EDUCATION && (
                  <button type="button" onClick={() => { setEducationList(l => [...l, {...EMPTY_EDU}]); setEducationProofFiles(f => [...f, null]); }} className="w-full py-4 border-2 border-dashed border-gray-300 rounded-2xl text-gray-400 font-bold hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex justify-center gap-2 items-center">
                    <Plus className="w-5 h-5"/> Add Qualification
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </TenantAdminLayout>
  );
};

export default EditStudent;